import {
  Button,
  CopyPlusIcon,
  Heading,
  IconButton,
  PencilIcon,
  PlusIcon,
  SkeletonItem,
  Tag,
  TagModes,
  TagSizes,
  TagVariants,
  TrashIcon,
  useModalContext
} from 'stubben-edge-unified-frontend-react-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@api-client/client';
import {
  campaignsQueryKeys,
  useCampaignDeleteMutation,
  useCampaignDuplicateMutation,
  useCampaignsListQuery
} from '@api-client/campaigns';
import DataTableProvider from '@/components/common/DataTableProvider';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import type { CampaignInterface } from '@/interfaces/Campaign.interface';
import {
  buildCampaignListFilters,
  campaignListStatusTagMode,
  formatCampaignListStartDate,
  formatCampaignStatusLabel,
  isCampaignDraftDeletable,
  mapCampaignListParams,
  resolveCampaignListOpenedMetric
} from './utils/campaignListUtils';
import CampaignListMetricCell from './components/CampaignListMetricCell';
import CampaignsEmptyState from './components/CampaignsEmptyState';
import { CAMPAIGN_CREATE_URL, CAMPAIGN_PENDING_POLL_MS } from './constants';
import { getCampaignViewDetails } from './CampaignView/utils/campaignViewDetails';
import DeleteModal from './components/modals/DeleteModal';
import DuplicateModal from './components/modals/DuplicateModal';
import {
  CAMPAIGN_DELETE_MODAL,
  CAMPAIGN_DUPLICATE_MODAL
} from '@/constants/modals';
import useStore from '@/hooks/useStore';

const DEFAULT_CAMPAIGN_SORT = {
  field: 'start_date',
  direction: 'desc' as const
};

function Campaigns() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const modal = useModalContext();
  const { toast } = useStore();
  const [campaignToDelete, setCampaignToDelete] =
    useState<CampaignInterface | null>(null);
  const [campaignToDuplicate, setCampaignToDuplicate] =
    useState<CampaignInterface | null>(null);
  const [outdatedTimestamp, setOutdatedTimestamp] = useState(0);
  const [hasPendingCampaign, setHasPendingCampaign] = useState(false);

  const { data: initialData, isLoading: isInitialLoading } =
    useCampaignsListQuery({
      listData: mapCampaignListParams({
        page: 1,
        perPage: 1,
        sort: DEFAULT_CAMPAIGN_SORT
      })
    });

  const isEmpty =
    !isInitialLoading && (initialData?.pagination?.total ?? 0) === 0;

  const { data: campaignTypes = [] } = useQuery({
    queryKey: ['campaignTypes'],
    queryFn: () => api.campaigns.listTypes(),
    enabled: !isInitialLoading && !isEmpty
  });

  const deleteMutation = useCampaignDeleteMutation({
    onSuccess: () => {
      modal.close(CAMPAIGN_DELETE_MODAL);
      setCampaignToDelete(null);
      setOutdatedTimestamp(Date.now());
      queryClient.invalidateQueries({ queryKey: campaignsQueryKeys.lists() });
      toast.success('Campaign deleted');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete campaign');
    }
  });

  const duplicateMutation = useCampaignDuplicateMutation({
    onSuccess: (createdCampaign) => {
      modal.close(CAMPAIGN_DUPLICATE_MODAL);
      setCampaignToDuplicate(null);
      setOutdatedTimestamp(Date.now());
      queryClient.invalidateQueries({ queryKey: campaignsQueryKeys.lists() });
      toast.success('Campaign duplicated');
      navigate(`/campaigns/${createdCampaign.uuid}`);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to duplicate campaign');
    }
  });

  const handleDeleteConfirm = () => {
    if (!campaignToDelete) return;
    deleteMutation.mutate({ uuid: campaignToDelete.uuid });
  };

  const handleDuplicateConfirm = () => {
    if (!campaignToDuplicate) return;
    duplicateMutation.mutate({ uuid: campaignToDuplicate.uuid });
  };

  return (
    <div className="flex flex-col gap-6" data-cy="campaigns-page">
      <div className="flex items-center justify-between">
        <Heading size="sm" testIdentifier="campaigns-page-heading">
          Campaigns
        </Heading>
        {!isEmpty && (
          <Button
            variant="primary"
            leftSlot={<PlusIcon size={20} />}
            testIdentifier="campaigns-page-create-campaign"
            href={CAMPAIGN_CREATE_URL}
          >
            Create campaign
          </Button>
        )}
      </div>

      {isInitialLoading ? (
        <SkeletonItem className="h-44 w-full rounded-xl" />
      ) : isEmpty ? (
        <CampaignsEmptyState />
      ) : (
        <DataTableProvider<CampaignInterface>
          tableId="campaigns"
          keyField="uuid"
          isSeparated
          searchable
          searchPlaceholder="Search campaigns"
          fetchResolver={async ({ searchTerm, ...listData }) =>
            api.campaigns.list(
              mapCampaignListParams({ searchTerm, ...listData }),
              searchTerm
            )
          }
          defaultSort={DEFAULT_CAMPAIGN_SORT}
          filtersList={buildCampaignListFilters(campaignTypes)}
          filterDrawerTitle="Filter campaigns"
          showDrawerResetFilters
          noResultsText="No campaigns found"
          outdatedTimestamp={outdatedTimestamp}
          refreshTime={
            hasPendingCampaign ? CAMPAIGN_PENDING_POLL_MS : undefined
          }
          onFetchComplete={(data) =>
            setHasPendingCampaign(
              data.items.some((campaign) => campaign.status === 'pending')
            )
          }
          onRowClicked={(row) => {
            navigate(`/campaigns/${row.uuid}`);
          }}
          useNewActionbar
          actionsHeaderTestId="campaigns-table-actions-header"
          actions={(row) => {
            const viewDetails = getCampaignViewDetails(row);

            return (
              <>
                <IconButton
                  size="sm"
                  title="Edit"
                  mode="ghost"
                  testIdentifier={`campaigns-row-${row.uuid}-edit`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/campaigns/${row.uuid}`);
                  }}
                >
                  <PencilIcon size={14} />
                </IconButton>
                {viewDetails.showDuplicateAction && (
                  <IconButton
                    size="sm"
                    title="Duplicate"
                    mode="ghost"
                    testIdentifier={`campaigns-row-${row.uuid}-duplicate`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCampaignToDuplicate(row);
                      modal.open(CAMPAIGN_DUPLICATE_MODAL);
                    }}
                  >
                    <CopyPlusIcon size={14} />
                  </IconButton>
                )}
                <IconButton
                  size="sm"
                  title="Delete"
                  mode="ghost"
                  disabled={!isCampaignDraftDeletable(row.status)}
                  testIdentifier={`campaigns-row-${row.uuid}-delete`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCampaignToDelete(row);
                    modal.open(CAMPAIGN_DELETE_MODAL);
                  }}
                >
                  <TrashIcon size={14} />
                </IconButton>
              </>
            );
          }}
          columns={[
            {
              name: 'Campaign name',
              selector: (row) => row.name,
              sortable: true,
              sortField: 'name',
              align: 'left'
            },
            {
              name: 'Campaign type',
              selector: (row) => row.campaign_type?.name ?? '—',
              sortable: true,
              sortField: 'campaign_type_id',
              align: 'center'
            },
            {
              name: 'Status',
              selector: (row) => (
                <Tag
                  size={TagSizes.SM}
                  variant={TagVariants.Faded}
                  mode={
                    campaignListStatusTagMode[row.status] ?? TagModes.Normal
                  }
                >
                  {formatCampaignStatusLabel(row.status)}
                </Tag>
              ),
              sortable: true,
              sortField: 'status',
              align: 'center'
            },
            {
              name: 'Start date',
              selector: (row) => formatCampaignListStartDate(row),
              sortable: true,
              sortField: 'start_date',
              align: 'center'
            },
            {
              name: 'Delivered',
              selector: (row) => (
                <CampaignListMetricCell
                  count={row.metrics_summary?.deliveredCount}
                  rate={row.metrics_summary?.deliveredRate}
                />
              ),
              align: 'center'
            },
            {
              name: 'Opened',
              selector: (row) => {
                const openedMetric = resolveCampaignListOpenedMetric(row);

                return (
                  <CampaignListMetricCell
                    count={openedMetric?.openedCount}
                    rate={openedMetric?.openedRate}
                  />
                );
              },
              align: 'center'
            },
            {
              name: 'Follow-ups',
              selector: (row) =>
                String(row.follow_up_count ?? row.follow_ups?.length ?? 0),
              align: 'center'
            }
          ]}
        />
      )}

      <DeleteModal
        onConfirm={handleDeleteConfirm}
        isPending={deleteMutation.isPending}
        campaignName={campaignToDelete?.name}
      />
      <DuplicateModal
        onConfirm={handleDuplicateConfirm}
        isPending={duplicateMutation.isPending}
        campaignName={campaignToDuplicate?.name}
      />
    </div>
  );
}

export default Campaigns;
